import { KeyboardEvent } from 'react';
import { Tooltip } from 'shared-ui';

import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';

import { DodStateName, DodStateTone, StateChipProps } from './StateChip.types';
import * as Styled from './StateChip.styles';

/**
 * Colour is never the only signal: every state carries its own word, so the chip survives a
 * greyscale print and a red/green colour deficiency. The tone is exposed as `data-tone` for tests.
 */
const STATE_TONE: Record<DodStateName, DodStateTone> = {
  idle: 'ok',
  draining: 'info',
  paused: 'warn',
  connected: 'ok',
  disconnected: 'bad',
  reconnecting: 'warn',
  error: 'bad',
  closed: 'ok',
  open: 'bad',
  half_open: 'warn',
  permanent: 'bad',
  transient: 'warn',
  operation: 'bad',
  'max-retries': 'warn',
  'unknown-cap': 'neutral',
  unknown: 'neutral',
};

const isKnownState = (state: string): state is DodStateName => Object.hasOwnProperty.call(STATE_TONE, state);

/**
 * The monitor's one chip: the application's status badge with a tone.
 *
 * A state chip (`state`) picks its tone and its word from the state vocabulary; a plain badge
 * (`tone` + `text`) is the same surface for an alert, a heartbeat age or a history status.
 */
export const StateChip = ({
  state,
  tone,
  text,
  label,
  caption,
  tooltip,
  onClick,
  className,
  testId,
  role,
}: StateChipProps): JSX.Element => {
  const { formatMessage } = useLocale();

  const known = state !== undefined && isKnownState(state);
  const resolvedTone: DodStateTone = tone ?? (known ? STATE_TONE[state] : 'neutral');

  // An unrecognised wire value is shown verbatim rather than flattened to "unknown": the operator
  // needs to see what the worker actually reported, and the neutral tone already says it is new.
  const word =
    text ??
    (known
      ? formatMessage({ id: AppMessages[`dod.common.state.${state}`] })
      : state || formatMessage({ id: AppMessages['dod.common.state.unknown'] }));

  const content = [label ? `${label}:` : '', word, caption ? `· ${caption}` : ''].filter(Boolean).join(' ');

  const onKeyDown = (event: KeyboardEvent<HTMLSpanElement>): void => {
    if (onClick && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onClick();
    }
  };

  const chip = (
    <Styled.Badge
      className={className}
      data-testid={testId ?? (state !== undefined ? `dod-state-chip-${state}` : undefined)}
      data-tone={resolvedTone}
      $tone={resolvedTone}
      $isClickable={!!onClick}
      onClick={onClick}
      onKeyDown={onClick ? onKeyDown : undefined}
      role={role ?? (onClick ? 'button' : undefined)}
      tabIndex={onClick ? 0 : undefined}
    >
      <Styled.Text variant="subtitle2" $tone={resolvedTone}>
        {content}
      </Styled.Text>
    </Styled.Badge>
  );

  return tooltip ? (
    <Tooltip title={tooltip} placement="top">
      {chip}
    </Tooltip>
  ) : (
    chip
  );
};
